import CardCommon from '@/pages/opinion/components/CardCommon';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Segment, Row, Select } from 'tea-component';
import useSelectOption, {
  mentionsDauOptions,
} from '../../hooks/useSelectOptions';
import { useBatchTypeRequest } from '../../hooks/useBatchTypeRequest';

import { sortTimeListFunc } from '@/pages/opinion/utils';
import BarStackCharts from '../ChartComponent/BarStackCharts';
import LineChart from '../ChartComponent/LineChart';

import useLanguage from '@/pages/opinion/hooks/useLanguage';
import { avgValueFormat, reviewsFormat } from '@/pages/opinion/utils/value';

export default function CommonChart({
  xAxisList,
  dau,
  commonParams,
  isNoMobile,
  title,
  selectTitle,
  requestFunc,
  defaultValue = 'total',
  hasSentiment = true,
  firstOptions,
  transformFunc = (value: string, type?: string) => value,
}: any) {
  const [sentimentValue, setSentimentValue] = useState(defaultValue);
  const [mentionValue, setMentionValue] = useState<'mentions' | 'mentions_dau'>(
    'mentions',
  );
  const [selectValue, setSelectValue] = useState(''); // 折线图的topic选中值

  const dealwithTransformFunc = (value: string) => {
    if (value?.toLocaleLowerCase() === 'others') {
      return formatMessage({ id: 'Others' });
    }
    return transformFunc(value, sentimentValue);
  };

  const {
    lineDataMap,
    barData: topicStacked,
    isLoading,
    isError,
    getBarLineDataStack,
  } = useBatchTypeRequest({
    requestFunc: requestFunc?.[sentimentValue] || requestFunc,
    xAxisList,
    dau,
    mentionValue,
  });

  useEffect(() => {
    if (Object.keys(commonParams).length > 0 && xAxisList?.length > 0) {
      const params = commonParams;
      if (hasSentiment) {
        params.sentiment_type = sentimentValue;
      }
      getBarLineDataStack(params);
    }
  }, [commonParams, sentimentValue, xAxisList]);

  const selectOption = useSelectOption(topicStacked, 'name', 'name', [
    topicStacked,
  ]);
  const topicTrendLine = useMemo(() => {
    const list = (lineDataMap[selectValue] || []).sort(
      sortTimeListFunc('time'),
    );
    return list;
  }, [selectValue, lineDataMap]);

  const mentionsDauOpts = mentionsDauOptions(commonParams);

  useEffect(() => {
    setSelectValue(selectOption?.[0]?.value);
  }, [selectOption]);

  const { formatMessage, formatOptionsMessage } = useLanguage();

  const formatValueFunc = (value: number) => {
    let multiple = 1,
      func = reviewsFormat,
      suffix = '';
    if (mentionValue === 'mentions_dau') {
      multiple = 10000;
      func = avgValueFormat;
      suffix = '‱';
    }
    const data = func(value * multiple);
    if (data === '-') return '-';
    return data + suffix;
  };

  const mentionValueOptions = useMemo(() => {
    return formatOptionsMessage(mentionsDauOpts);
  }, [mentionsDauOpts]);

  const titleName = useMemo(() => {
    if (mentionValue === 'mentions_dau') {
      const item = mentionValueOptions.find(
        (item) => item.value === mentionValue,
      );
      return item?.transText;
    }
    return 'Mentions';
  }, [mentionValue]);

  return (
    <CardCommon
      className="dbo-display-card"
      title={formatMessage({ id: title })}
    >
      <Row>
        <Col span={12}>
          <Segment
            className="display-segment"
            value={sentimentValue}
            onChange={(value) => setSentimentValue(value)}
            options={formatOptionsMessage(
              firstOptions || [
                { text: 'Total-2', value: 'total' },
                { text: 'Negative', value: 'negative' },
                { text: 'Positive', value: 'positive' },
              ],
            )}
          />
          {!isNoMobile && (
            <Segment
              className="display-segment"
              value={mentionValue}
              onChange={(value: any) => setMentionValue(value)}
              options={mentionValueOptions}
            />
          )}
          <BarStackCharts
            data={topicStacked}
            mentionTab={mentionValue}
            xAxisList={xAxisList}
            commonParams={commonParams}
            loading={isLoading}
            title={titleName}
            transformFunc={dealwithTransformFunc}
            onClick={(value: string) => {
              setSelectValue(value);
            }}
          ></BarStackCharts>
        </Col>
        <Col span={12}>
          <Select
            appearance="button"
            options={selectOption.map((item) => ({
              text: dealwithTransformFunc(item.text),
              value: item.value,
            }))}
            matchButtonWidth
            value={selectValue}
            className="display-select"
            button={
              <>
                <span className="select-button-text">
                  {formatMessage({
                    id: selectTitle?.[sentimentValue] || selectTitle,
                  })}
                  :{' '}
                </span>
                {dealwithTransformFunc(selectValue)}
              </>
            }
            onChange={(value) => setSelectValue(value)}
          />
          <LineChart
            xAxisList={xAxisList}
            data={topicTrendLine}
            filter={commonParams}
            mentionTab={mentionValue}
            loading={isLoading}
            title={titleName}
            transformFunc={dealwithTransformFunc}
            formatValue={formatValueFunc}
          ></LineChart>
        </Col>
      </Row>
    </CardCommon>
  );
}
